/**
 * 恒星容许度（逐颗）纯逻辑模块。
 *
 * 只做一件事：把「恒星配置弹窗」提交的数组整理成可持久化、可下发星盘的映射
 * `{ starNum: 度 }`。
 *
 * 口径与星盘服务（`astrolabe_micronaut`）保持三处一致，改动必须两边同步：
 * 1. **默认值 1.0 度**：对应 `BOStarInfo.tolerance` 的字段初值与
 *    `StarToleranceContext.DEFAULT_STAR_TOLERANCE`；未配置即视为 1.0；
 * 2. **合法区间 (0, 90] 度**：非法值（非数字 / 0 / 负数 / 超上限）一律按未配置处理，
 *    避免脏配置把恒星报警放大成「几乎每颗星都报警」；
 * 3. **只保留非默认值**：把 1.0 写进配置没有意义（未配置就等于 1.0），
 *    因此只落用户真正改过的恒星 —— 211 颗恒星全量约 3~4KB，只落差异通常只有几条。
 *
 * 本模块不依赖 window / axios / Vue，便于 node 直跑断言。
 */

/** 未配置时的默认容许度（度），必须与星盘服务 `BOStarInfo.tolerance` 初值一致 */
const DEFAULT_STAR_TOLERANCE = 1.0

/** 容许度上限（度），与星盘服务 `StarToleranceContext.MAX_STAR_TOLERANCE` 一致 */
const MAX_STAR_TOLERANCE = 90.0

/** 判定「是否等于默认值」的容差：弹窗按 度+分/60+秒/3600 换算，正常不会产生尾差 */
const DEFAULT_EPSILON = 1e-9

/** 落库精度：与 astrolabeRequestPayload.normalizeNumericMap 的 6 位小数口径一致 */
const PRECISION = 6

/** 是否为合法的容许度：有限数字且落在 (0, 90] */
function isValidTolerance (value) {
  const tolerance = Number(value)
  if (!Number.isFinite(tolerance)) {
    return false
  }
  return tolerance > 0 && tolerance <= MAX_STAR_TOLERANCE
}

/** 是否等于默认值（1.0 度） */
function isDefaultTolerance (value) {
  return Math.abs(Number(value) - DEFAULT_STAR_TOLERANCE) < DEFAULT_EPSILON
}

/**
 * 把弹窗提交的恒星数组整理成 `{ starNum: 度 }`。
 *
 * @param {Array<{id: number, tolerance: number}>} list 弹窗 `update:matrix` 的载荷
 * @returns {Object} 只含「用户改过且合法」的恒星；无有效项时返回 `{}`
 */
function buildStarToleranceMap (list) {
  const map = {}
  ;(Array.isArray(list) ? list : []).forEach(item => {
    if (!item || item.id === undefined || item.id === null) {
      return
    }
    const tolerance = Number(item.tolerance)
    if (!isValidTolerance(tolerance) || isDefaultTolerance(tolerance)) {
      return
    }
    map[String(item.id)] = Number(tolerance.toFixed(PRECISION))
  })
  return map
}

/** 是否为空映射（等价于「全部使用默认容许度」） */
function isEmptyStarToleranceMap (map) {
  return !map || Object.keys(map).length === 0
}

module.exports = {
  DEFAULT_STAR_TOLERANCE,
  MAX_STAR_TOLERANCE,
  isValidTolerance,
  isDefaultTolerance,
  buildStarToleranceMap,
  isEmptyStarToleranceMap
}
